import type React from "react";
import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { requestUangJalanReportRevisionApi } from "../../api/uangJalanReportsApi";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import { getErrorMessage } from "../../utils/errorHandling";
import { toastEmitter } from "../../utils/toastEmitter";

type TranslationKeys = keyof typeof translations.en;

interface ReportRequestRevisionDialogProps {
  reportId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

const ReportRequestRevisionDialog: React.FC<ReportRequestRevisionDialogProps> = ({
  reportId,
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [revisionNote, setRevisionNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setRevisionNote("");
      setError(null);
    }
  }, [open]);

  const revisionNoteTrimmed = revisionNote.trim();

  const handleSubmit = async () => {
    if (!revisionNoteTrimmed) {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await requestUangJalanReportRevisionApi(reportId, revisionNoteTrimmed);
      await onSuccess();
      toastEmitter.emit(t("reports.revisionRequestedSuccessfully" as TranslationKeys), "success");
      onClose();
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "requestUangJalanReportRevision");
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-testid={TestId.UJR.REQUEST_REVISION_DIALOG}
    >
      <DialogTitle>{t("orders.requestRevision" as TranslationKeys)}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("reports.reportRevisionDialogHint" as TranslationKeys)}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          required
          label={t("orders.revisionNote" as TranslationKeys)}
          value={revisionNote}
          onChange={(event) => setRevisionNote(event.target.value)}
          inputProps={{ "data-testid": TestId.UJR.REQUEST_REVISION_NOTE }}
        />
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t("Cancel" as TranslationKeys)}</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={submitting || !revisionNoteTrimmed}
          onClick={handleSubmit}
          data-testid={TestId.UJR.REQUEST_REVISION_REPORT}
        >
          {t("orders.requestRevision" as TranslationKeys)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportRequestRevisionDialog;
